import { useState, useEffect } from 'react'
import { backfillService } from '../services/backfillService'
import {
  ACTION_TYPE_LABELS,
  TRIGGER_TYPE_LABELS,
  CATEGORY_DISPLAY_NAMES,
  CATEGORY_COLORS,
} from '../types'
import type { BenefitRule } from '../types'

interface BackfillRulesStepProps {
  category: string
  onConfirm: (selectedRuleIds: number[]) => void
  onBack: () => void
}

export function BackfillRulesStep({ category, onConfirm, onBack }: BackfillRulesStepProps) {
  const [rules, setRules] = useState<BenefitRule[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchRules = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await backfillService.getRulesForCategory(category)
        if (!cancelled) {
          setRules(data)
          setSelectedIds(new Set(data.map((r) => r.id)))
        }
      } catch {
        if (!cancelled) setError('Error al cargar reglas')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchRules()
    return () => { cancelled = true }
  }, [category])

  const toggleRule = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selectedIds.size === rules.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(rules.map((r) => r.id)))
    }
  }

  const colors = CATEGORY_COLORS[category]

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse h-24 bg-gray-100 rounded-lg" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        {error}
      </div>
    )
  }

  if (rules.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">
          No hay reglas activas elegibles para el nivel{' '}
          <span className="font-semibold">{CATEGORY_DISPLAY_NAMES[category]}</span>.
        </p>
        <button onClick={onBack} className="btn btn-secondary mt-4">
          Volver
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-gray-600">
            Selecciona las reglas que deseas asignar a los clientes{' '}
            <span className={`inline-block px-2 py-0.5 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}>
              {CATEGORY_DISPLAY_NAMES[category]}
            </span>
          </p>
        </div>
        <button
          onClick={toggleAll}
          className="text-sm text-primary-600 hover:text-primary-800 font-medium"
        >
          {selectedIds.size === rules.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
        </button>
      </div>

      <div className="space-y-3">
        {rules.map((rule) => {
          const isSelected = selectedIds.has(rule.id)
          return (
            <label
              key={rule.id}
              className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                isSelected
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleRule(rule.id)}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900">{rule.name}</span>
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {ACTION_TYPE_LABELS[rule.action_type] ?? rule.action_type}
                  </span>
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    {TRIGGER_TYPE_LABELS[rule.trigger_type] ?? rule.trigger_type}
                  </span>
                </div>
                {rule.description && (
                  <p className="text-sm text-gray-500 mt-1">{rule.description}</p>
                )}
              </div>
            </label>
          )
        })}
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t">
        <button onClick={onBack} className="btn btn-secondary">
          Volver
        </button>
        <button
          onClick={() => onConfirm(Array.from(selectedIds))}
          disabled={selectedIds.size === 0}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Ver preview ({selectedIds.size} regla{selectedIds.size !== 1 ? 's' : ''})
        </button>
      </div>
    </div>
  )
}
